import * as React from 'react'
import { useCallback } from 'react'

import {
  Alert,
  Box,
  Spinner,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { ContractInformationStepV2Document } from 'generated/resources/graphql'

import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track } from 'modules/core/lib/Tracking'
import centsToDecimal from 'modules/core/lib/formatCents'
import i18n from 'modules/core/lib/i18n'

import { parseSalary } from 'modules/employees/components/UnifiedAddEmployeeModal/ContractInformationStep/salaryUtils'
import { CheckedMoneyField } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/CheckedMoneyField'
import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'
import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { genericAmplitudeProperties } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { groupFieldsByGroup } from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { useLegalEntityRoles } from 'modules/employees/hooks/useLegalEntityRoles'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'
import { useFinancialWorkspaceSeeAgreements } from 'modules/finance/components/FinancialWorkspace/hooks/useFinancialWorkspaceSeeAgreements'
import {
  formatSalaryFromCents,
  selectReferenceSalaryRange,
} from 'modules/job_catalog/lib/salaryUtils'
import { validateField } from 'modules/job_catalog/lib/workingConditions'

import SalaryInRangeCallout from './SalaryInRangeCallout'
import { usePrefillSalaryInitialValue } from './prefillSalary'

const COUNTRY_CODES_WITH_SPECIFIC_FIELDS = ['es', 'fr', 'de', 'pt']

const workingConditionsFieldsErrors = (data: WizardData) => {
  const errors = {
    contractsMaxLegalYearlyHoursError: validateField(
      'ANNUAL_HOURS',
      Number(data.contractsMaxLegalYearlyHours) / 100 || null
    ),
    contractsMaximumWeeklyHoursError: validateField(
      'WEEKLY_HOURS',
      Number(data.contractsMaximumWeeklyHours) / 100 || null
    ),
  }

  return Object.values(errors).some((error) => error !== undefined)
}

const isCompletedStep = (data: WizardData) => {
  if (!data.contractsStartsOn) return false
  if (!data.workingHours && !!data.workingHoursFrequency) return false
  if (!!data.workingHours && !data.workingHoursFrequency) return false

  const hasWorkingConditionsFieldsErrors = workingConditionsFieldsErrors(data)
  if (hasWorkingConditionsFieldsErrors) return false

  const isSalaryFieldValid = (!!data.salaryAmount && !!data.isSalaryValid) || !data.salaryAmount
  return isSalaryFieldValid
}

interface State {
  workingHoursError: boolean
  workingHoursFrequencyError: boolean
  contractsMaxLegalYearlyHoursError: string | undefined
  contractsMaximumWeeklyHoursError: string | undefined
}

const ContractInformationStepV2 = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [state, setState]: [State, React.Dispatch<React.SetStateAction<State>>] =
    React.useState<State>({
      workingHoursError: false,
      workingHoursFrequencyError: false,
      contractsMaxLegalYearlyHoursError: undefined,
      contractsMaximumWeeklyHoursError: undefined,
    })

  const { data: contractInformationData } = useQuery(ContractInformationStepV2Document, {
    variables: {
      companyId: data.companyId ? parseInt(data.companyId, 10) : 0,
      legalEntityId: data.legalEntityId ? parseInt(data.legalEntityId, 10) : 0,
      nodeUuid: data.jobCatalogTreeNodeUuid || '',
    },
    skip: !data.hasDevJobCatalogTree || !data.jobCatalogTreeNodeUuid,
  })

  const legalEntityCurrency =
    contractInformationData?.companies?.legalEntitiesConnection?.nodes.find(
      (node) => node.id === parseInt(data.legalEntityId || '0', 10)
    )?.currency

  const salaryRanges = contractInformationData?.jobCatalog?.nodeAttributesConnection?.nodes
    .filter((node) => node.valueSalaryRange && node.takesPrecedence)
    .map((node) => node.valueSalaryRange)

  const precedenceSalaryRange = selectReferenceSalaryRange(
    salaryRanges?.filter(Boolean) || [],
    data.locationId ? parseInt(data.locationId, 10) : undefined,
    legalEntityCurrency || ''
  )

  const precedenceWorkingConditions =
    contractInformationData?.jobCatalog?.nodeAttributesConnection?.nodes.find(
      (node) => node.valueWorkingConditions && node.takesPrecedence
    )?.valueWorkingConditions ?? undefined

  const [rolesCollection, isLoadingRoles, _refetchRoles, canSeeJobCatalog] = useLegalEntityRoles(
    !!data.legalEntityId ? +data.legalEntityId : data.legalEntity?.id
  )
  const { canSeeFinanceWorkspace } = useFinancialWorkspaceSeeAgreements()

  const fields =
    data.fieldsSchema?.filter(
      (field) =>
        field.category === 'agreement_information' || field.category === 'payroll_information'
    ) || []
  const contractDetailsFields = fields
    ?.filter((field) => field.section === 'contract_details')
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

  const getCountrySpecificFields = useCallback(
    (countryCode?: string) => {
      if (!countryCode) return []

      switch (countryCode) {
        case 'es':
          return (
            data.fieldsSchema?.filter(
              (field) => field.category === 'agreement_information_spain'
            ) || []
          )
        case 'fr':
          return (
            data.fieldsSchema?.filter(
              (field) => field.category === 'agreement_information_france'
            ) || []
          )
        case 'de':
          return (
            data.fieldsSchema?.filter(
              (field) => field.category === 'agreement_information_germany'
            ) || []
          )
        case 'pt':
          return (
            data.fieldsSchema?.filter(
              (field) => field.category === 'agreement_information_portugal'
            ) || []
          )
        default:
          return []
      }
    },
    [data.fieldsSchema]
  )

  const filteredContractDetailsFields = useMemo(() => {
    return contractDetailsFields?.filter((field) => {
      if (
        (field.fieldName === 'salary_amount' || field.fieldName === 'salary_frequency') &&
        !canSeeJobCatalog
      ) {
        return false
      }

      if (field.fieldName === 'trial_period_ends_on' && !data.hasTrialPeriod) {
        return false
      }

      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeJobCatalog, data.hasTrialPeriod, data.hasDevTimeTrackingWorkableDaysDefinition])
  const contractDetailsGroupedFields = groupFieldsByGroup(filteredContractDetailsFields)
  const employeeInformationFields = [
    ...getCountrySpecificFields(data.legalEntityCountry),
    ...fields,
  ].filter((field) => field.section === 'employment_information')

  const filteredEmployeeInformationFields = employeeInformationFields.filter((field) => {
    if (
      !data.hasDevTimeTrackingWorkableDaysDefinition &&
      field.fieldName === 'contracts_working_time_percentage_in_cents'
    ) {
      return false
    }

    return true
  })

  const sortedEmployeeInformationFields = useMemo(() => {
    const contractTypeFieldNames = [
      'contracts_es_contract_type_id',
      'contracts_fr_contract_type_id',
      'contracts_pt_contract_type_id',
      'contracts_de_contract_type_id',
    ]

    return [...filteredEmployeeInformationFields].sort((a, b) => {
      const aIsContractType = contractTypeFieldNames.includes(a.fieldName)
      const bIsContractType = contractTypeFieldNames.includes(b.fieldName)
      const aIsDiscontinuous = a.fieldName === 'contract_is_discontinuous'
      const bIsDiscontinuous = b.fieldName === 'contract_is_discontinuous'

      return (
        Number(bIsContractType) - Number(aIsContractType) ||
        Number(bIsDiscontinuous) - Number(aIsDiscontinuous)
      )
    })
  }, [filteredEmployeeInformationFields.length])

  const employmentInformationGroupedFields = groupFieldsByGroup(sortedEmployeeInformationFields)
  const workingHoursFields = fields
    ?.filter((field) => field.section === 'working_hours')
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  const filteredWorkingHoursFields = workingHoursFields.filter((field) => {
    if (
      !data.hasDevTimeTrackingWorkableDaysDefinition &&
      (field.fieldName === 'contracts_bank_holiday_treatment' ||
        field.fieldName === 'contracts_max_legal_yearly_hours' ||
        field.fieldName === 'contracts_maximum_weekly_hours')
    ) {
      return false
    }

    if (field.fieldName === 'contracts_annual_working_time_distribution') {
      return !!data.hasTimeTrackingAnnualisationDailyValue && !!data.contractsMaxLegalYearlyHours
    }

    return true
  })

  const isLevelSelected = data.jobLevelId
  const role = rolesCollection && data.roleId ? rolesCollection[+data.roleId] : undefined
  const level =
    role && data.jobLevelId
      ? role.levels.find((roleLevel) => roleLevel.id.toString() === data.jobLevelId)
      : undefined
  const salaryRangeFromLevel =
    level &&
    level.salaryRanges.find(
      (salaryRange) => salaryRange.salaryFrequency === data.workingHoursFrequency
    )
  const { salaryFrequency, minRange, maxRange, currency, amountType } = salaryRangeFromLevel || {}
  const minRangeUI = formatSalaryFromCents(minRange, data.legalEntityCountry, currency)
  const maxRangeUI = formatSalaryFromCents(maxRange, data.legalEntityCountry, currency)
  const salaryOutOfRange = () => {
    const salaryAmount = parseSalary(data.salaryAmount)
    if (!salaryAmount || !minRange || !maxRange) return

    const minRangeDecimal = centsToDecimal(minRange)
    const maxRangeDecimal = centsToDecimal(maxRange)

    switch (amountType) {
      case 'range':
        return salaryAmount < minRangeDecimal || salaryAmount > maxRangeDecimal
      case 'fixed':
        return salaryAmount !== minRangeDecimal
      default:
        false
    }
  }

  const salaryAlertMessage =
    amountType === 'range'
      ? i18n.t('employee_form.wizard.role_step.salary_range_alert', {
          min_range: minRangeUI,
          max_range: maxRangeUI,
          salary_frequency: salaryFrequency,
        })
      : i18n.t('employee_form.wizard.role_step.salary_fixed_alert', {
          fixed_amount: minRangeUI,
          salary_frequency: salaryFrequency,
        })

  const onClickToggle = (fieldName: string, value: boolean) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const onSalaryAmountChange = (salary: string, frequency?: string) => {
    const salaryAmount = parseSalary(salary)
    const isSalaryValid = !!salaryAmount && salaryAmount >= 0

    onDataChanged({
      ...data,
      salaryAmount: salary,
      isSalaryValid,
      contractsSalaryFrequency: frequency || data.contractsSalaryFrequency,
    })
  }

  usePrefillSalaryInitialValue(data, onSalaryAmountChange, precedenceSalaryRange)

  const validateWorkingHours = (hours?: string, frequency?: string) => {
    if (!hours && !!frequency) {
      setState({
        ...state,
        workingHoursError: true,
      })
    }
    if (!frequency && !!hours) {
      setState({
        ...state,
        workingHoursFrequencyError: true,
      })
    }

    const hasHoursAndFrequency = !!hours && !!frequency
    const hasNoHoursAndFrequency = !hours && !frequency

    if (hasHoursAndFrequency || hasNoHoursAndFrequency) {
      setState({ ...state, workingHoursError: false, workingHoursFrequencyError: false })
    }
  }

  const getFieldError = (fieldName: string): string | undefined => {
    switch (fieldName) {
      case 'salaryAmount':
        return !data.isSalaryValid && !!data.salaryAmount
          ? i18n.t('add_employee_modal.contract_information_step.fields.salary_amount.error')
          : ''
      case 'workingHours':
        return state.workingHoursError
          ? i18n.t('add_employee_modal.contract_information_step.fields.hours.error')
          : ''
      case 'workingHoursFrequency':
        return state.workingHoursFrequencyError
          ? i18n.t('add_employee_modal.contract_information_step.fields.frequency.error')
          : ''
      case 'contractsMaxLegalYearlyHours':
        return state.contractsMaxLegalYearlyHoursError
      case 'contractsMaximumWeeklyHours':
        return state.contractsMaximumWeeklyHoursError
    }
  }

  const handleFieldValidation = (fieldName: string, value: string) => {
    if (fieldName === 'workingHours') {
      return validateWorkingHours(value, data.workingHoursFrequency)
    }
    if (fieldName === 'workingHoursFrequency') {
      return validateWorkingHours(data.workingHours, value)
    }
    if (fieldName === 'contractsMaxLegalYearlyHours') {
      const error = validateField('ANNUAL_HOURS', Number(value) / 100 || null)

      setState({
        ...state,
        contractsMaxLegalYearlyHoursError: error,
      })
    }
    if (fieldName === 'contractsMaximumWeeklyHours') {
      const error = validateField('WEEKLY_HOURS', Number(value) / 100 || null)

      setState({
        ...state,
        contractsMaximumWeeklyHoursError: error,
      })
    }
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
    handleFieldValidation(fieldName, value)
  }

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addContractInformation, genericAmplitudeProperties(data))
  })

  if (isLoadingRoles)
    return (
      <Box flexGrow justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.contract_information_step.section.contract_details')}
          </Text.Header2>
          {Object.entries(contractDetailsGroupedFields ?? {}).map(([group, groupFields]) => (
            <Box key={group} flexDirection="row" gap="s16" alignItems="flexEnd">
              {group === 'salary' && (
                <CheckedMoneyField
                  label={i18n.t(
                    'add_employee_modal.contract_information_step.fields.salary_amount.label'
                  )}
                  fieldName="salaryAmount"
                  frequencyFieldName="contractsSalaryFrequency"
                  groupFields={groupFields}
                  data={data}
                  referenceSalary={precedenceSalaryRange}
                  onChange={onSalaryAmountChange}
                  error={getFieldError('salaryAmount')}
                />
              )}
              {group !== 'salary' &&
                groupFields.map((field) => (
                  <Fields
                    key={field.fieldName}
                    field={field}
                    data={data}
                    onChangeField={onChangeField}
                    getFieldError={getFieldError}
                    onMoneyChange={onSalaryAmountChange}
                    onClickToggle={onClickToggle}
                    precedenceSalaryRange={precedenceSalaryRange}
                  />
                ))}
            </Box>
          ))}
        </Box>
        {COUNTRY_CODES_WITH_SPECIFIC_FIELDS.includes(data.legalEntityCountry || '') && (
          <Box flexDirection="column" gap="s16">
            <Text.Header2>
              {i18n.t('add_employee_modal.contract_information_step.section.employee_information')}
            </Text.Header2>
            {Object.entries(employmentInformationGroupedFields ?? {}).map(
              ([group, groupFields]) => (
                <Box key={group} flexDirection="row" gap="s16">
                  {groupFields.map((field) => (
                    <Fields
                      key={field.fieldName}
                      field={field}
                      data={data}
                      onChangeField={onChangeField}
                      onClickToggle={onClickToggle}
                    />
                  ))}
                </Box>
              )
            )}
          </Box>
        )}
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.contract_information_step.section.working_hours')}
          </Text.Header2>
          {filteredWorkingHoursFields.map((field) => (
            <Fields
              key={field.fieldName}
              field={field}
              data={data}
              onChangeField={onChangeField}
              workingConditions={precedenceWorkingConditions}
              getFieldError={getFieldError}
            />
          ))}
        </Box>
      </Box>
      <Box flexDirection="column" paddingTop="s24" paddingRight="s4" maxWidth="s240">
        {data.hasDevJobCatalogTree &&
          data.roleLevelName &&
          precedenceSalaryRange &&
          !data.contractIsDiscontinuous && (
            <SalaryInRangeCallout
              precedenceSalaryRange={precedenceSalaryRange}
              salaryAmount={parseSalary(data.salaryAmount) || 0}
              salaryFrequency={data.contractsSalaryFrequency || ''}
              roleLevelName={data.roleLevelName || ''}
              legalEntityCountry={data.legalEntityCountry || ''}
              locationId={data.locationId}
            />
          )}
        {data.hasDevJobCatalogTree && data.roleLevelName && precedenceWorkingConditions && (
          <Box paddingTop="s120">
            <Alert color="info">
              {i18n.t('employee_form.wizard.contract_step.alerts.working_conditions', {
                working_conditions_name: precedenceWorkingConditions.name,
              })}
            </Alert>
          </Box>
        )}
        {!data.hasDevJobCatalogTree && (
          <Box paddingTop="s8">
            {isLevelSelected && salaryRangeFromLevel && (
              <Alert color="warning">
                <Box flexDirection="column" gap="s4">
                  {salaryOutOfRange() && (
                    <Text.Normal weight="bold">
                      {i18n.t('employee_form.wizard.role_step.salary.out_of_range')}
                    </Text.Normal>
                  )}
                  {amountType === 'fixed' && (
                    <Text.Normal weight="bold">
                      💡{i18n.t('employee_form.wizard.role_step.salary_fixed_alert_title')}
                    </Text.Normal>
                  )}
                  {salaryAlertMessage}
                </Box>
              </Alert>
            )}
          </Box>
        )}
        {canSeeFinanceWorkspace && (
          <Box paddingTop="s8" height="s140">
            <Alert color="info">
              {i18n.t('finance.financial_workspace.launch_info.agreements')}
            </Alert>
          </Box>
        )}
        {data.contractIsDiscontinuous && (
          <Box style={{ marginTop: '140px' }}>
            <Alert color="info">
              <Box flexDirection="column" gap="s4">
                <Text.Normal weight="bold">
                  {i18n.t(
                    'add_employee_modal.contract_information_step.alerts.fixed_discontinuos.title'
                  )}
                </Text.Normal>
                <Text.Normal>
                  {i18n.t(
                    'add_employee_modal.contract_information_step.alerts.fixed_discontinuos.description'
                  )}
                </Text.Normal>
              </Box>
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export const contractInformationStepV2 = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.contract_information_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <ContractInformationStepV2 {...props} />,
  isCompleted: isCompletedStep,
})
